import { X } from 'lucide-react'
import { forwardRef } from 'react'

import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { assets as uiAssets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { type InputProps } from '@/ui/atoms/input/Input'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { parseBigNumber } from '@/utils/bigNumber'
import { decimalNumberRegex, normalizeDecimalInput } from '@/utils/decimalInput'
import BigNumber from 'bignumber.js'
import { truncateWithEllipsis } from '@/domain/common/format'
import { BalanceType } from '@/features/dialogs/common/types'
import Image from 'next/image'

export type AssetInputProps = {
  token: Token
  className?: string | undefined
  onRemove?: () => void
  setMax?: () => void
  isMaxSelected?: boolean
  balance?: NormalizedUnitNumber
  balanceType?: BalanceType
  disabled?: boolean
  error?: string
  value: string
  variant?: 'usd' | 'crypto'
  walletIconLabel?: string
  readOnly?: boolean
} & InputProps

export const AssetInput = forwardRef<HTMLInputElement, AssetInputProps>(
  (
    {
      token,
      className,
      onRemove,
      setMax,
      balance,
      balanceType,
      disabled,
      error,
      value,
      onChange,
      variant = 'crypto',
      walletIconLabel,
      isMaxSelected,
      readOnly,
      ...rest
    },
    ref,
  ) => {
    const inputValue = (() => {
      if (isMaxSelected) {
        const valueAsBigNumber = BigNumber(value)
        if (!valueAsBigNumber.isNaN()) {
          return valueAsBigNumber.dp(6).toFixed()
        }
      }

      return value
    })()

    return (
      <div className="flex-1">
        <div
          className={cn(
            'flex h-14 min-w-[10rem] flex-row rounded-sm border bg-input py-2 pr-2 pl-4',
            disabled && !readOnly && 'opacity-70',
            error && 'mb-1 border-red-400 bg-red-400/10',
            className,
          )}
        >
          <div className="flex grow flex-col">
            <input
              type="text"
              inputMode="decimal"
              className={cn('flex bg-transparent focus:outline-none', error && 'text-red-400')}
              ref={ref}
              placeholder="0"
              id="asset-input"
              disabled={disabled}
              size={1} // force minimum width
              value={inputValue}
              data-testid={testIds.component.AssetInput.input}
              {...rest}
              onChange={(e) => {
                const normalizedValue = normalizeDecimalInput(e.target.value)
                if (normalizedValue === null) return

                e.target.value = normalizedValue
                const decimalPart = e.target.value.split('.')[1] || ''
                if (decimalPart.length > token.decimals) {
                  const truncated = `${e.target.value.split('.')[0]}.${decimalPart.slice(0, token.decimals)}`
                  e.target.value = truncated
                }
                if (decimalNumberRegex.test(e.target.value) || e.target.value === '') {
                  onChange?.(e)
                }
              }}
            />
            {token && (
              <Typography variant="prompt">
                {truncateWithEllipsis(
                  token.formatUSD(NormalizedUnitNumber(parseBigNumber(value, 0)), {
                    compact: true,
                  }),
                  28,
                )}
              </Typography>
            )}
          </div>
          <div className="flex flex-row">
            <div className="flex flex-col items-end">
              {setMax && (
                <Button
                  onClick={disabled ? undefined : setMax}
                  className={cn('p-1', balance && 'p-0 text-xs')}
                  disabled={isMaxSelected}
                  variant="text"
                  data-testid={testIds.component.AssetInput.maxButton}
                >
                  MAX
                </Button>
              )}
              {balance && (
                <div className="mt-auto flex flex-row items-center">
                  {walletIconLabel && (
                    <Typography variant="prompt" className="mr-1">
                      {walletIconLabel}
                    </Typography>
                  )}

                  {getBalanceIcon(balanceType)}

                  <Typography variant="prompt">
                    {variant === 'crypto'
                      ? token!.format(balance, { style: 'compact' })
                      : token?.formatUSD(balance, { compact: true })}
                  </Typography>
                </div>
              )}
            </div>
            {onRemove && (
              <Button
                onClick={disabled ? undefined : onRemove}
                className={cn('p-1 text-icon-foreground')}
                variant="text"
              >
                <X />
              </Button>
            )}
          </div>
        </div>
        {error && (
          <Typography data-testid={testIds.component.AssetInput.error} variant="prompt" className="text-red-400">
            {error}
          </Typography>
        )}
      </div>
    )
  },
)

AssetInput.displayName = 'AssetInput'

// Allow optional leading digits to enable typing "0.1" or ".1"
function getBalanceIcon(balanceType?: BalanceType) {
  switch (balanceType) {
    case 'protocol':
      return <Image className="mr-1" src={uiAssets.actions.deposit} alt="deposited" />
    default:
      return <Image className="mr-1" src={uiAssets.wallet} alt="wallet" />
  }
}
